import { useEffect, useLayoutEffect, useRef, useCallback, type RefObject } from "react";
import paper from "paper";
import {
  type DrawNodeTranslations,
  drawNode,
  getViewportBounds,
  clearPositionCache,
  type ViewportBounds,
} from "../drawNode/drawNode";
import type { TreeNodeData } from "../TreeViewDataLayer";

interface UseCanvasRenderingProps {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  paperInitializedRef: RefObject<boolean>;
  canvasSize: { width: number; height: number };
  aboData: TreeNodeData | null;
  handleNodeSelect: (abo: TreeNodeData) => void;
  toggleExpand: (targetId: string) => void;
  translations: DrawNodeTranslations;
  selectedNodeId: string | null;
  zoom: number;
  pan: { x: number; y: number };
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  handleWheelZoom: (deltaY: number, cursorOffset: { x: number; y: number }) => void;
  hideVolume?: boolean;
}

/**
 * Hook to handle canvas rendering with viewport culling for performance optimization.
 * Only renders nodes visible in the current viewport.
 */
export const useCanvasRendering = ({
  canvasRef,
  paperInitializedRef,
  canvasSize,
  aboData,
  handleNodeSelect,
  toggleExpand,
  translations,
  selectedNodeId,
  zoom,
  pan,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  handleWheelZoom,
  hideVolume = false,
}: UseCanvasRenderingProps): void => {
  const prevAboDataRef = useRef<TreeNodeData | null>(null);
  const redrawFrameRef = useRef<number | null>(null);
  const prevZoomRef = useRef(zoom);
  const prevPanRef = useRef(pan);
  // Tracks whether the fit-to-view zoom/pan has been drawn yet. The first application
  // (the initial fit, or a fullscreen re-fit that resets this via key remount) must draw
  // synchronously before paint so the canvas is never revealed showing the stale zoom-1
  // bitmap for a frame — that one-frame stale paint is the "zoom in then zoom out" flicker.
  const hasDrawnFitRef = useRef(false);

  const redrawCanvas = useCallback(
    (viewport: ViewportBounds | null) => {
      if (!canvasRef.current || !paperInitializedRef.current || canvasSize.width === 0 || !aboData) return;

      paper.project.clear();
      drawNode(
        aboData,
        canvasSize.width / 2,
        80,
        handleNodeSelect,
        toggleExpand,
        translations,
        selectedNodeId,
        viewport,
        hideVolume,
      );
    },
    [
      aboData,
      canvasSize.width,
      handleNodeSelect,
      toggleExpand,
      translations,
      selectedNodeId,
      canvasRef,
      paperInitializedRef,
      hideVolume,
    ],
  );

  useEffect(() => {
    if (!canvasRef.current || !paperInitializedRef.current || canvasSize.width === 0 || !aboData) return;

    if (prevAboDataRef.current !== aboData) {
      clearPositionCache();
      prevAboDataRef.current = aboData;
    }

    const currentZoom = paper.view?.zoom ?? 1;
    const currentCenter = paper.view?.center?.clone();

    const viewport = getViewportBounds();
    redrawCanvas(viewport);

    if (paper.view) {
      paper.view.zoom = currentZoom;
      if (currentCenter) paper.view.center = currentCenter;
    }
  }, [aboData, selectedNodeId, canvasSize, redrawCanvas, canvasRef, paperInitializedRef]);

  // useLayoutEffect (not useEffect) so the initial fit draw runs after DOM mutation but
  // before the browser paints — in the same commit that flips the canvas to display:block.
  // With a passive effect the canvas could be revealed showing the stale zoom-1 bitmap for
  // one frame before the deferred rAF redraw fired, causing the intermittent zoom flicker.
  useLayoutEffect(() => {
    if (!canvasRef.current || !paper.view || canvasSize.width === 0 || !aboData) return;

    const zoomChanged = prevZoomRef.current !== zoom;
    const panChanged = prevPanRef.current !== pan;
    prevZoomRef.current = zoom;
    prevPanRef.current = pan;

    // If only canvasSize changed (e.g. iOS browser chrome show/hide during scroll),
    // preserve the existing view center rather than recalculating from the new height.
    if (!zoomChanged && !panChanged) return;

    paper.view.zoom = zoom;

    const ceoNodeY = 80;
    const viewCenterY = canvasSize.height / 2;
    const offsetY = ceoNodeY - viewCenterY;

    paper.view.center = new paper.Point(canvasSize.width / 2 - pan.x, canvasSize.height / 2 - pan.y + offsetY);

    // Draw the first (fit) application synchronously so the revealed bitmap is already at
    // the correct zoom. All subsequent interactive zoom/pan (drag, pinch, wheel) stay on the
    // rAF-coalesced path to keep gestures smooth.
    if (!hasDrawnFitRef.current) {
      hasDrawnFitRef.current = true;
      if (redrawFrameRef.current !== null) {
        cancelAnimationFrame(redrawFrameRef.current);
        redrawFrameRef.current = null;
      }
      const viewport = getViewportBounds();
      redrawCanvas(viewport);
      paper.view.update();
      return;
    }

    if (redrawFrameRef.current === null) {
      redrawFrameRef.current = requestAnimationFrame(() => {
        redrawFrameRef.current = null;
        const viewport = getViewportBounds();
        redrawCanvas(viewport);
        paper.view?.update();
      });
    }
  }, [zoom, pan, canvasSize, aboData, redrawCanvas, canvasRef]);

  // Cancels any pending redraw frame on unmount only — a cleanup on the effect above
  // would run on every dep change and cancel the coalescing frame before it can fire.
  useEffect(() => {
    return () => {
      if (redrawFrameRef.current !== null) {
        cancelAnimationFrame(redrawFrameRef.current);
        redrawFrameRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onTouchStartNative = (e: TouchEvent) => handleTouchStart(e as unknown as React.TouchEvent);
    const onTouchMoveNative = (e: TouchEvent) => handleTouchMove(e as unknown as React.TouchEvent);
    const onTouchEndNative = (e: TouchEvent) => handleTouchEnd(e as unknown as React.TouchEvent);

    canvas.addEventListener("touchstart", onTouchStartNative, { passive: false });
    canvas.addEventListener("touchmove", onTouchMoveNative, { passive: false });
    canvas.addEventListener("touchend", onTouchEndNative, { passive: false });

    return () => {
      canvas.removeEventListener("touchstart", onTouchStartNative);
      canvas.removeEventListener("touchmove", onTouchMoveNative);
      canvas.removeEventListener("touchend", onTouchEndNative);
    };
  }, [canvasRef, handleTouchStart, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleWheelEvent = (e: WheelEvent) => {
      // Only treat wheel input as zoom when a zoom modifier is held (Ctrl/Cmd), or on trackpad
      // pinch (browsers synthesize ctrlKey on pinch wheel events). Otherwise scroll the page
      // manually — the container's touch-action/overflow rules prevent the browser's native
      // scroll chaining from reaching the page on its own.
      if (!e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
        window.scrollBy({ top: e.deltaY, left: e.deltaX, behavior: "instant" });
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const rect = canvas.getBoundingClientRect();
      const cursorOffset = {
        x: e.clientX - rect.left - canvasSize.width / 2,
        y: e.clientY - rect.top - canvasSize.height / 2,
      };
      handleWheelZoom(e.deltaY, cursorOffset);
    };

    canvas.addEventListener("wheel", handleWheelEvent, { passive: false, capture: true });
    return () => canvas.removeEventListener("wheel", handleWheelEvent, { capture: true });
  }, [canvasRef, canvasSize, handleWheelZoom]);
};
